/**
 * 森林绿灰度开关解析（REQ-20260618-0003）
 *
 * 决定当前是否启用森林绿 UI（data-ui="forest"）。优先级：
 *   1. 本地覆盖 localStorage('ui_skin')  —— 开发 / 演示 / 一键回退
 *   2. 构建期环境变量 VITE_UI_SKIN
 *   3. 租户主题配置 extraConfig.ui_skin   —— 线上按租户灰度（不新增字段）
 *   4. 默认 'default'（原 AntD 观感）
 *
 * extraConfig 在后端为 JSONB，前端可能拿到对象或字符串，两种都兼容。
 */

export type UiSkin = 'forest' | 'default';

interface ThemeLike {
  extraConfig?: unknown;
}

function safeParse(input: unknown): Record<string, unknown> | null {
  if (!input) return null;
  if (typeof input === 'object') return input as Record<string, unknown>;
  if (typeof input === 'string') {
    try {
      const parsed = JSON.parse(input);
      return typeof parsed === 'object' && parsed ? (parsed as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }
  return null;
}

export function resolveUiSkin(config?: ThemeLike | null): UiSkin {
  // 1. 本地覆盖（最高优先级，便于演示与一键回退）
  try {
    const ls = localStorage.getItem('ui_skin');
    if (ls === 'forest' || ls === 'default') return ls;
  } catch {
    /* localStorage 不可用时忽略 */
  }

  // 2. 构建期默认
  const envSkin = (import.meta as unknown as { env?: Record<string, string> })?.env?.VITE_UI_SKIN;
  if (envSkin === 'forest') return 'forest';

  // 3. 租户配置 extraConfig.ui_skin
  const extra = safeParse(config?.extraConfig);
  if (extra?.ui_skin === 'forest') return 'forest';

  return 'default';
}

/** 当前 document 是否处于森林绿模式（供组件读取） */
export function isForestUI(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.getAttribute('data-ui') === 'forest';
}

/**
 * 把服务端配置的 ui_skin 同步到 localStorage 本地覆盖。
 * REQ-20260619-0002: resolveUiSkin 的 localStorage 优先级最高，应用预设/重置等
 * 显式操作后必须调用本函数，否则旧的 localStorage 会盖掉预设携带的 ui_skin，
 * 导致"应用森林绿预设却不生效"。使两入口（界面风格开关 / 预设）状态一致。
 */
export function syncUiSkinToLocal(config?: ThemeLike | null): void {
  const extra = safeParse(config?.extraConfig);
  const skin: UiSkin = extra?.ui_skin === 'forest' ? 'forest' : 'default';
  try {
    localStorage.setItem('ui_skin', skin);
  } catch {
    /* localStorage 不可用时忽略 */
  }
}
